import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useQueryErrorResetBoundary, useSuspenseQuery} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {IssueParticipants} from '@github-ui/server-persisted-queries'
import {useAnalytics} from '@github-ui/use-analytics'
import {InfoIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useMemo, useState} from 'react'

import {renderGenericError} from '../shared/IssueViewerError'
import {type ParticipantNode, participantsQueryOptions} from './fetch-participants'
import styles from './ParticipantsList.module.css'

type ParticipantsListProps = {
  repo: string
  owner: string
  number: number
  totalCount: number
}

export function ParticipantsList({repo, owner, number, totalCount}: ParticipantsListProps) {
  const {reset} = useQueryErrorResetBoundary()
  const [retryKey, setRetryKey] = useState(0)

  // Reset the errored query and remount the boundary so the list refetches,
  // matching the retry behavior of the previous PreloadedQueryBoundary.
  const retry = () => {
    reset()
    setRetryKey(key => key + 1)
  }

  return (
    <ErrorBoundary key={retryKey} boundaryName="ParticipantsList" fallback={renderGenericError(retry)}>
      <ParticipantsListInternal owner={owner} repo={repo} number={number} totalCount={totalCount} />
    </ErrorBoundary>
  )
}

function ParticipantsListInternal({repo, owner, number, totalCount}: ParticipantsListProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: IssueParticipants,
        sendAnalyticsEvent,
        maxAgeMs: ISSUE_VIEWER_CACHE_TTL_MS,
        // A restored list may be stale within its TTL, so revalidate after restoring it.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )

  const {data: participants} = useSuspenseQuery(participantsQueryOptions(owner, repo, number, persister))

  return (
    <>
      <ActionList>
        {participants.map(participant => (
          <ParticipantItem key={participant.login} participant={participant} />
        ))}
      </ActionList>
      {totalCount > 100 && (
        <div className={styles.participantLimitNotice}>
          <InfoIcon size={16} />
          Only the first 100 participants are shown in this list.
        </div>
      )}
    </>
  )
}

function ParticipantItem({participant}: {participant: ParticipantNode}) {
  const {login, avatarUrl, name} = participant
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttributes = getHovercardAttributesForType('user', {login})

  return (
    <ActionList.LinkItem href={`/${login}`} {...hovercardAttributes}>
      <ActionList.LeadingVisual>
        <GitHubAvatar src={avatarUrl} size={20} alt={`@${login}`} className={styles.participantAvatar} />
      </ActionList.LeadingVisual>
      {name}
      <ActionList.Description truncate>{login}</ActionList.Description>
    </ActionList.LinkItem>
  )
}
